import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Droplets, Euro, Network, TrendingDown, Building2, Zap,
  ExternalLink, MapPin, BarChart2,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
  ResponsiveContainer, Cell,
} from 'recharts';
import { datosSectorApi } from '../services/api';

const ICONOS = {
  droplets: Droplets, euro: Euro, network: Network,
  'trending-down': TrendingDown, building: Building2, zap: Zap,
};

const COLORES = {
  blue: { borde: 'border-blue-200', valor: 'text-blue-600', icono: 'text-blue-500' },
  orange: { borde: 'border-orange-300', valor: 'text-orange-600', icono: 'text-orange-500' },
  gray: { borde: 'border-gray-200', valor: 'text-gray-800', icono: 'text-gray-400' },
  red: { borde: 'border-red-300', valor: 'text-red-600', icono: 'text-red-500' },
  green: { borde: 'border-green-200', valor: 'text-green-600', icono: 'text-green-500' },
  purple: { borde: 'border-purple-200', valor: 'text-purple-600', icono: 'text-purple-500' },
};

const AZUL = '#2563EB';
const VERDE = '#16A34A';
const NARANJA = '#F97316';

// Hex por color (para bordes construidos dinámicamente — Tailwind JIT no genera clases runtime).
const COLOR_HEX = { blue: '#2563EB', orange: '#F97316', gray: '#9CA3AF', red: '#EF4444', green: '#16A34A', purple: '#9333EA' };

const parseES = (s) => Number(String(s ?? '').replace(/\./g, '').replace(',', '.'));
const num = (n) => (typeof n === 'number' ? n.toLocaleString('es-ES') : (n ?? '—'));
const pct = (n) => (Number.isFinite(n) ? `${n > 0 ? '+' : ''}${n.toFixed(1).replace('.', ',')}%` : '—');

function KpiCard({ kpi }) {
  const Icon = ICONOS[kpi.icono] || BarChart2;
  const c = COLORES[kpi.color] || COLORES.gray;
  return (
    <div className={`bg-white border ${c.borde} rounded-xl p-4 shadow-sm flex flex-col`}>
      <div className="flex items-start justify-between">
        <span className="text-xs font-medium text-gray-500">{kpi.titulo}</span>
        <Icon className={`w-5 h-5 shrink-0 ${c.icono}`} />
      </div>
      <div className={`mt-2 text-3xl font-bold leading-none ${c.valor}`}>
        {kpi.valor}<span className="ml-1 text-sm font-medium text-gray-400">{kpi.unidad}</span>
      </div>
      {kpi.nota && <p className="mt-1.5 text-[11px] text-gray-500 leading-snug flex-1">{kpi.nota}</p>}
      <a
        href={kpi.url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-700 hover:underline"
      >
        {kpi.fuente} · {kpi['año']} <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
}

function GraficoCCAA({ titulo, data, media, colorBase, seleccion, unidad }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700 mb-2">{titulo}</h3>
      <ResponsiveContainer width="100%" height={470}>
        <BarChart layout="vertical" data={data} margin={{ left: 10, right: 24, top: 8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 11 }} />
          <YAxis type="category" dataKey="name" width={158} tick={{ fontSize: 10 }} interval={0} />
          <Tooltip formatter={(v) => [`${num(v)} ${unidad}`, 'Valor']} />
          <ReferenceLine
            x={media}
            stroke="#EF4444"
            strokeDasharray="4 4"
            label={{ value: `Media ${num(media)}`, position: 'insideTopRight', fontSize: 10, fill: '#EF4444' }}
          />
          <Bar dataKey="valor" radius={[0, 4, 4, 0]}>
            {data.map((d) => (
              <Cell key={d.codigo} fill={d.codigo === seleccion ? NARANJA : colorBase} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

const AYUDAS = [
  {
    titulo: 'PERTE Digitalización del Ciclo del Agua',
    texto: '1.200 M€ · 3 convocatorias resueltas.',
    url: 'https://planderecuperacion.gob.es',
    color: 'purple',
  },
  {
    titulo: 'Déficit de inversión del sector',
    texto: '4.485 M€/año necesarios vs 2.415 M€ reales. La inversión real es solo el 54 % de lo necesario.',
    url: 'https://www.daquas.es',
    color: 'red',
  },
  {
    titulo: 'Directiva Marco del Agua (DMA)',
    texto: 'Exige recuperación de costes reales. España está un 43 % por debajo del precio europeo.',
    url: 'https://www.miteco.gob.es',
    color: 'blue',
  },
];

export default function SectorAgua() {
  const [kpis, setKpis] = useState([]);
  const [ccaa, setCcaa] = useState([]);
  const [sel1, setSel1] = useState('');
  const [sel2, setSel2] = useState('');
  const [verNacional, setVerNacional] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const [nac, lst] = await Promise.all([datosSectorApi.nacional(), datosSectorApi.ccaa()]);
        if (!vivo) return;
        setKpis(nac.kpis || []);
        setCcaa(lst.items || []);
      } catch (e) {
        if (vivo) setError(e.message || 'Error cargando datos del sector');
      } finally {
        if (vivo) setLoading(false);
      }
    })();
    return () => { vivo = false; };
  }, []);

  const consumoNac = useMemo(() => parseES(kpis.find((k) => k.id === 'consumo')?.valor) || 121, [kpis]);
  const costeNac = useMemo(() => parseES(kpis.find((k) => k.id === 'coste')?.valor) || 2.23, [kpis]);

  const dataConsumo = useMemo(() => ccaa
    .map((c) => ({ name: c.ccaa, valor: c.consumo_litros_hab_dia, codigo: c.codigo_ccaa }))
    .sort((a, b) => a.valor - b.valor), [ccaa]);
  const dataCoste = useMemo(() => ccaa
    .map((c) => ({ name: c.ccaa, valor: c.coste_total_eur_m3, codigo: c.codigo_ccaa }))
    .sort((a, b) => a.valor - b.valor), [ccaa]);

  const c1 = ccaa.find((c) => c.codigo_ccaa === sel1);
  const c2 = ccaa.find((c) => c.codigo_ccaa === sel2);

  const filas = [
    { ind: 'Consumo (L/hab/día)', nac: consumoNac, v1: c1?.consumo_litros_hab_dia, v2: c2?.consumo_litros_hab_dia, base: consumoNac },
    { ind: 'Coste (€/m³)', nac: costeNac, v1: c1?.coste_total_eur_m3, v2: c2?.coste_total_eur_m3, base: costeNac },
    { ind: 'Variación bienal (%)', nac: null, v1: c1?.variacion_bienal_pct, v2: c2?.variacion_bienal_pct, base: null, esPct: true },
  ];
  const th = 'px-3 py-2 text-left font-semibold text-gray-500';
  const td = 'px-3 py-2 border-t border-gray-100';

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <header className="mb-6 border-b border-gray-200 pb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-3xl font-bold text-gray-900">El Sector del Agua en España</h1>
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-900 text-white">Datos 2024–2025</span>
        </div>
        <p className="mt-1 text-sm text-gray-500">Datos y estadísticas del sector</p>
      </header>

      {loading && <p className="text-sm text-gray-400 py-10 text-center">Cargando…</p>}
      {error && <p className="text-sm text-red-600 py-6 text-center">{error}</p>}

      {!loading && !error && (
        <>
          {/* BLOQUE 1 — KPIs */}
          <section className="mb-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {kpis.map((k) => <KpiCard key={k.id} kpi={k} />)}
            </div>
          </section>

          {/* BLOQUE 2 — Comparativa por CCAA */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">Comparativa por Comunidad Autónoma</h2>
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <select
                value={sel1}
                onChange={(e) => setSel1(e.target.value)}
                className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white text-gray-700"
                data-testid="select-ccaa-1"
              >
                <option value="">Comunidad Autónoma 1</option>
                {ccaa.map((c) => <option key={c.codigo_ccaa} value={c.codigo_ccaa}>{c.ccaa}</option>)}
              </select>
              <select
                value={sel2}
                onChange={(e) => setSel2(e.target.value)}
                className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white text-gray-700"
                data-testid="select-ccaa-2"
              >
                <option value="">Comunidad Autónoma 2 (opcional)</option>
                {ccaa.filter((c) => c.codigo_ccaa !== sel1).map((c) => (
                  <option key={c.codigo_ccaa} value={c.codigo_ccaa}>{c.ccaa}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setVerNacional((v) => !v)}
                className={`text-sm px-3 py-1.5 rounded-md border transition-colors ${
                  verNacional ? 'bg-brand-600 text-white border-brand-600' : 'text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Comparar con media nacional
              </button>
            </div>

            {!c1 && !c2 ? (
              <p className="text-sm text-gray-400 py-6 text-center border border-dashed border-gray-200 rounded-lg">
                Elige una o dos comunidades autónomas para comparar sus indicadores.
              </p>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase">
                    <tr>
                      <th className={th}>Indicador</th>
                      {verNacional && <th className={th}>Nacional</th>}
                      {c1 && <th className={th}>{c1.ccaa}</th>}
                      {c2 && <th className={th}>{c2.ccaa}</th>}
                      {c1 && !c2 && <th className={th}>Dif.</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filas.map((f) => {
                      const dif = (f.base && f.v1 != null && !f.esPct) ? (f.v1 / f.base - 1) * 100 : null;
                      return (
                        <tr key={f.ind}>
                          <td className={`${td} font-medium text-gray-700`}>{f.ind}</td>
                          {verNacional && (
                            <td className={`${td} text-gray-600`}>{f.nac == null ? '—' : num(f.nac)}</td>
                          )}
                          {c1 && (
                            <td className={`${td} font-semibold text-gray-900`}>
                              {f.v1 == null ? '—' : (f.esPct ? pct(f.v1) : num(f.v1))}
                            </td>
                          )}
                          {c2 && (
                            <td className={`${td} font-semibold text-gray-900`}>
                              {f.v2 == null ? '—' : (f.esPct ? pct(f.v2) : num(f.v2))}
                            </td>
                          )}
                          {c1 && !c2 && (
                            <td className={`${td} font-semibold ${dif == null ? 'text-gray-400' : dif > 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {dif == null ? '—' : pct(dif)}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* BLOQUE 3 — Gráficos */}
          <section className="mb-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <GraficoCCAA
              titulo="Consumo de agua por CCAA (L/hab/día)"
              data={dataConsumo} media={consumoNac} colorBase={AZUL} seleccion={sel1} unidad="L/hab/día"
            />
            <GraficoCCAA
              titulo="Coste del agua por CCAA (€/m³)"
              data={dataCoste} media={costeNac} colorBase={VERDE} seleccion={sel1} unidad="€/m³"
            />
          </section>

          {/* BLOQUE 4 — Ayudas y financiación */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">Ayudas y financiación</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {AYUDAS.map((a) => {
                const c = COLORES[a.color] || COLORES.gray;
                return (
                  <div
                    key={a.titulo}
                    className="bg-white border border-gray-200 rounded-lg p-4"
                    style={{ borderLeft: `4px solid ${COLOR_HEX[a.color] || COLOR_HEX.gray}` }}
                  >
                    <p className={`text-sm font-bold ${c.valor}`}>{a.titulo}</p>
                    <p className="mt-1 text-xs text-gray-600 leading-relaxed">{a.texto}</p>
                    <a href={a.url} target="_blank" rel="noopener noreferrer"
                       className="mt-2 inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-700 hover:underline">
                      Más información <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                );
              })}
            </div>
          </section>

          {/* FOOTER */}
          <div className="border-t border-gray-200 pt-4">
            <Link to="/mapa-municipios" className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700 hover:underline">
              <MapPin className="w-4 h-4" /> Ver municipios y operadores en el mapa →
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
